import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { LAST_TEST_REQUESTED_OPTIONS, LastTestRequestedGroup } from '../../shared/models/last-test-requested.model';
import { Filter } from '../../shared/filter/filter';
import { AgreementStatusFilter, LastTopUpRange, Role, UserSearchEvent } from '../../shared/models/user.model';
import { LocalStorageService } from '../../shared/services/localStorage.service';
import { UsersService } from '../../shared/services/users.service';
import { ValidationService } from '../../shared/services/validation.service';

interface RoleObject {
  id: string;
  label: string;
  isSelected: boolean;
}

interface AgreementStatusObject {
  id: AgreementStatusFilter;
  label: string;
}

interface LastTopUpRangeObject {
  id: LastTopUpRange;
  label: string;
}

@Component({
  standalone: false,
  selector: 'app-users-search',
  templateUrl: './users-search.component.html',
  styleUrls: ['./users-search.component.scss'],
})
export class UsersSearchComponent implements OnInit, OnDestroy {
  @Output() onSubmit = new EventEmitter<UserSearchEvent>();

  searchFormControl: FormControl = new FormControl();
  searchSubscription: Subscription;

  isBeingDragged = false;
  filters: Filter[] = [
    { filterKey: 'enabled', title: 'Enabled', show: true },
    { filterKey: 'lastTopUpRanges', title: 'Last top up', show: true },
    { filterKey: 'agreementStatus', title: 'Agreement status', show: true },
    { filterKey: 'roles', title: 'Roles', show: true },
    { filterKey: 'owner', title: 'Owner', show: true },
    { filterKey: 'company', title: 'Company', show: true },
    { filterKey: 'externalUserId', title: 'External User ID', show: true },
    { filterKey: 'paymentType', title: 'Payment Type', show: true },
    { filterKey: 'comment', title: 'Comment', show: true },
    { filterKey: 'departmentName', title: 'Business unit/department', show: true },
    { filterKey: 'rankingIndexes', title: 'Ranking index', show: true },
    { filterKey: 'lastTestRequestedGroups', title: 'Last test requested', show: true },
  ];

  roles: RoleObject[] = [
    { id: Role.ADMIN, label: 'Admin user', isSelected: false },
    { id: Role.MAIN, label: 'Main account', isSelected: false },
    { id: Role.DEPUTY, label: 'Deputy account', isSelected: false },
    { id: Role.SUB, label: 'Subaccount', isSelected: false },
  ];

  agreementStatuses: AgreementStatusObject[] = [
    // { id: AgreementStatusFilter.HAS_AGREEMENT, label: 'Yes', iconClass: 'far fa-smile text-success-s2' },
    { id: AgreementStatusFilter.NO_AGREEMENT, label: 'Not signed and no ClickTerm enabled' },
  ];

  readonly lastTestRequestedOptions = LAST_TEST_REQUESTED_OPTIONS;

  readonly lastTopUpRanges: LastTopUpRangeObject[] = [
    { id: LastTopUpRange.UNDER_1_MONTH, label: 'Under 1 month ago' },
    { id: LastTopUpRange.MONTHS_1_TO_3, label: '1–3 months ago' },
    { id: LastTopUpRange.MONTHS_3_TO_6, label: '3–6 months ago' },
    { id: LastTopUpRange.MONTHS_6_TO_12, label: '6–12 months ago' },
    { id: LastTopUpRange.MONTHS_12_TO_24, label: '12–24 months ago' },
    { id: LastTopUpRange.OVER_24_MONTHS, label: 'Over 24 months ago' },
    { id: LastTopUpRange.NEVER, label: 'Never' },
  ];

  readonly rankingIndexes = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  form: FormGroup;

  constructor(
    private formBuilder: FormBuilder,
    public validationService: ValidationService,
    private usersService: UsersService,
    private localStorage: LocalStorageService
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      enabled: [null],
      lastTestRequestedGroups: [[]],
      lastTopUpRanges: [[]],
      agreementStatus: [[]],
      roles: [[]],
      owner: [null],
      company: [null],
      externalUserId: [null],
      paymentType: [null],
      comment: [null],
      departmentName: [null],
      rankingIndexes: [[]],
    });
    this.restoreFilterLayout();
    this.searchSubscription = this.searchFormControl.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
      this.onSubmitClick();
    });
  }

  setSearchFormControlValue(value: string): void {
    this.searchFormControl.setValue(value);
  }

  getEnableValue(): boolean | null {
    return this.form.get('enabled')?.value;
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.filters, event.previousIndex, event.currentIndex);
    this.saveFilterLayout();
  }

  getIconForRole(role: string): string {
    switch (role) {
      case Role.ADMIN:
        return 'icon-admin';
      case Role.MAIN:
        return 'icon-chess-king';
      case Role.DEPUTY:
        return 'icon-chess-queen';
      case Role.SUB:
        return 'icon-chess-pawn';
      default:
        return '';
    }
  }

  onClickedEnabled(): void {
    const enabledFormControl: FormControl = this.form.get('enabled') as FormControl;
    enabledFormControl.setValue(enabledFormControl.value ? null : true);
    this.onSubmitClick();
  }

  onClickDisabled(): void {
    const enabledFormControl: FormControl = this.form.get('enabled') as FormControl;
    enabledFormControl.setValue(enabledFormControl.value === false ? null : false);
    this.onSubmitClick();
  }

  onChangeAgreementStatusSelection(agreementStatus: AgreementStatusFilter): void {
    this.onChangeListSelection('agreementStatus', agreementStatus);
  }

  onChangeListSelection(key: string, value: AgreementStatusFilter | LastTopUpRange | LastTestRequestedGroup | number): void {
    const agreementStatusFormControl: FormControl = this.form.get(key) as FormControl;
    const selectedAgreementStatusValue = (agreementStatusFormControl.value || []) as (
      | AgreementStatusFilter
      | LastTopUpRange
      | LastTestRequestedGroup
      | number
    )[];
    if (selectedAgreementStatusValue.includes(value)) {
      agreementStatusFormControl.setValue(selectedAgreementStatusValue.filter(status => status !== value));
    } else {
      agreementStatusFormControl.setValue([...selectedAgreementStatusValue, value]);
    }
    this.onSubmitClick();
  }

  isAgreementStatusSelected(agreementStatus: AgreementStatusFilter): boolean {
    return (this.form.get('agreementStatus')?.value || []).includes(agreementStatus);
  }

  onChangeRoleSelection(selectedRole: RoleObject): void {
    const roleFormControl: FormControl = this.form.get('roles') as FormControl;
    selectedRole.isSelected = !selectedRole.isSelected;
    roleFormControl.setValue(this.roles.filter(role => role.isSelected).map(role => role.id));
    this.onSubmitClick();
  }

  onChangePaymentSelection(paymentType: string): void {
    const paymentTypeFormControl: FormControl = this.form.get('paymentType') as FormControl;
    const previousPaymentType = paymentTypeFormControl.value;
    paymentTypeFormControl.setValue(previousPaymentType === paymentType ? null : paymentType);
    this.onSubmitClick();
  }

  getPeekForSelectedValues(filter): number | string | null {
    switch (filter.filterKey) {
      case 'roles':
        return this.roles.filter(role => role.isSelected).length;
      case 'lastTopUpRanges':
      case 'lastTestRequestedGroups':
      case 'rankingIndexes':
      case 'agreementStatus':
        return (this.form.get(filter.filterKey)?.value || []).length;
      default:
        return null;
    }
  }

  isFilterActive(filter: Filter): boolean {
    switch (filter.filterKey) {
      case 'enabled':
        return this.form.get('enabled')?.value !== null;
      case 'lastTopUpRanges':
      case 'lastTestRequestedGroups':
      case 'rankingIndexes':
      case 'agreementStatus':
        return (this.form.get(filter.filterKey)?.value || []).length > 0;
      case 'roles':
        return this.roles.filter(role => role.isSelected).length > 0;
      case 'owner':
        return this.form.get('owner').value?.length > 0;
      case 'company':
        return this.form.get('company').value?.length > 0;
      case 'externalUserId':
        return this.form.get('externalUserId').value?.length > 0;
      case 'paymentType':
        return this.form.get('paymentType').value?.length > 0;
      case 'comment':
        return this.form.get('comment').value?.length > 0;
      case 'departmentName':
        return this.form.get('departmentName').value?.length > 0;
      default:
        return false;
    }
  }

  onClear(filterKey: string) {
    switch (filterKey) {
      case 'owner':
        this.form.get('owner').reset();
        break;
      case 'company':
        this.form.get('company').reset();
        break;
      case 'externalUserId':
        this.form.get('externalUserId').reset();
        break;
      case 'comment':
        this.form.get('comment').reset();
        break;
      case 'departmentName':
        this.form.get('departmentName').reset();
        break;
      case 'lastTopUpRanges':
      case 'lastTestRequestedGroups':
      case 'rankingIndexes':
      case 'agreementStatus':
        this.form.get(filterKey).setValue([]);
        break;
      default:
        break;
    }
    this.onSubmitClick();
  }

  onSubmitClick() {
    const searchData: UserSearchEvent = {
      searchString: this.searchFormControl.value,
      ...this.form.getRawValue(),
    };
    if (!searchData.agreementStatus?.length) {
      searchData.agreementStatus = null;
    }
    if (searchData.externalUserId?.length) {
      searchData.provider = 'clickterm';
    } else {
      delete searchData.provider;
    }
    this.onSubmit.emit(searchData);
  }

  resetAll(): void {
    this.form.reset();
    this.roles.forEach(role => (role.isSelected = false));
    this.searchFormControl.reset();
  }

  deleteItem(event: CdkDragDrop<string[]>) {
    const indexToHide = event.previousIndex;
    this.filters[indexToHide].show = false;
    this.onClear(this.filters[indexToHide].filterKey);
    this.saveFilterLayout();
  }

  onClearAllFilter(): void {
    this.saveFilterLayout();
    this.resetAll();
    this.onSubmitClick();
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  private saveFilterLayout(): void {
    this.localStorage.set(`users_${this.usersService.authUser.id}_filter_state`, this.filters);
  }

  // Applies the saved order and visibility; titles always come from the code, saved filters that
  // no longer exist are dropped, and filters added since the layout was saved go to the end.
  private restoreFilterLayout(): void {
    const userId = this.usersService.authUser?.id;
    if (!userId) {
      return;
    }
    const savedFilters: Filter[] = this.localStorage.get(`users_${userId}_filter_state`) || [];
    const saved = new Map(savedFilters.map((filter, index) => [filter.filterKey, { index, show: filter.show }]));
    this.filters = this.filters
      .map((filter, index) => ({ filter, order: saved.get(filter.filterKey)?.index ?? savedFilters.length + index }))
      .sort((a, b) => a.order - b.order)
      .map(({ filter }) => ({ ...filter, show: saved.get(filter.filterKey)?.show ?? filter.show }));
  }
}
